import { NgTemplateOutlet } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { IntersectionObserverArea } from './intersection-observer-area';

export interface IntersectionObserverTemplateContext<T> {
  $implicit: T;
  visible: boolean;
}

@Component({
  selector: 'rt-intersection-observer-entry',
  template: `
    <ng-container *ngTemplateOutlet="template; context: context"></ng-container>
    <ng-content />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class IntersectionObserverEntryComponent {
  @Input() public forceVisible: boolean = false;
  @Input() public template: TemplateRef<IntersectionObserverTemplateContext<any>>;
  @Input() public templateContext: any;
  @Input() public firstPaintOnly: boolean = false;
  // NOTE: Use 'asyncRender' option with caution;
  // currently there is no queue mechanism implemented,
  // you might end up scheduling multiple callbacks for single rAF tick
  // which can lead to performance issues
  @Input() public asyncRender: boolean = false;

  @Output() public visibilityChange = new EventEmitter<boolean>();

  public get context(): IntersectionObserverTemplateContext<any> {
    return {
      $implicit: this.templateContext,
      visible: this.forceVisible || this.visible,
    };
  }

  public visible: boolean;
  private rafTick: number;
  private ngOnDestroySubject = new Subject<void>();
  private ngOnDestroy$: Observable<void> = this.ngOnDestroySubject.asObservable();

  constructor(
    private area: IntersectionObserverArea,
    private elRef: ElementRef,
    private cdRef: ChangeDetectorRef,
  ) {
    if (area.serviceAvailable) {
      this.visible = false;
    } else {
      this.visible = true;
      this.forceVisible = true;
      this.firstPaintOnly = true;
    }
  }

  public ngAfterViewInit(): void {
    if (this.area.serviceAvailable) {
      this.area
        .intersectionChange(this.elRef)
        .pipe(takeUntil(this.ngOnDestroy$))
        .subscribe({
          next: ({ visible }) => this.changeVisibility(visible),
          error: _ => this.changeVisibility(true),
        });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroySubject.next();
    this.ngOnDestroySubject.complete();

    if (this.rafTick) {
      cancelAnimationFrame(this.rafTick);
    }

    this.area.unobserve(this.elRef.nativeElement);
  }

  private changeVisibility(visible: boolean): void {
    if (this.visible && (this.forceVisible || this.firstPaintOnly)) {
      return;
    }

    if (this.visible !== visible) {
      this.visibilityChange.emit(visible);
      if (this.rafTick) cancelAnimationFrame(this.rafTick);

      this.visible = visible;

      if (this.asyncRender) {
        this.rafTick = requestAnimationFrame(() => {
          // @ts-ignore
          if (!this.cdRef['destroyed']) {
            this.cdRef.detectChanges();
          }
        });
      } else {
        this.cdRef.detectChanges();
      }
    }
  }
}
